import React, { useState, useContext, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { TitleText, LabelText } from "../../ui/Text";
import { Formik, Field } from "formik";
import { DashOperatingScore } from "../../components/OperatingScore/DashboardOperatingScore";
import { OperatingStatistics } from "./OperatingStatistics";
import { TasksContent } from "./TasksContent";
import { Button } from "../../ui/Button";
import { LaggingMeasuresContent } from "./LaggingMeasuresContent";
import { IndicatorIcon } from "../../assets/svg/IndicatorIcon";
import Modal from "../../ui/Modal";
import { UpdateMeasures } from "./UpdateMeasures";
import { GetHook } from "../../api/api";
import { SCORECARD_URL } from "../../api/urls";
import { AuthContext } from "../../context/AuthContext";
import { get } from "../../utils/get";
import axios from "../../utils/axios";
import moment from "moment";
import numeral from "numeral";
import DateRangePicker2 from "../../ui/DateRangePicker";
import MeasuresTab from "../scorecard/MeasuresTab";
import CustomTabs from "../../ui/CustomTabs";
import useAuth from "../../hooks/useAuth";

const columns = [
  {
    Header: "Measure name",
    accessor: "kpi_name",
    Cell: ({ row }) => (
      <>
        <IndicatorIcon
          color={get(row, "original.score") >= 2 ? "#EDBC0C" : "#FF6666"}
        />
        <LabelText style={{ fontWeight: "bold" }} color="#214160">
          {get(row, "original.kpi_name")}
        </LabelText>
      </>
    ),
  },
  {
    Header: "Score",
    accessor: "target_score",
    Cell: ({ row }) => (
      <LabelText
        color={get(row, "original.score") >= 2 ? "#EDBC0C" : "#FF6666"}
        style={{ fontWeight: "bold" }}
      >
        {get(row, "original.score")}
      </LabelText>
    ),
  },
  {
    Header: "Weighted Score",
    accessor: "normalised_weight",
    Cell: ({ row }) => (
      <LabelText style={{ fontWeight: "bold" }} color="#214160">
        {get(row, "original.normalised_weight")}
      </LabelText>
    ),
  },
  {
    Header: "Actual",
    accessor: "total_value",
    Cell: ({ row }) => (
      <LabelText
        color={get(row, "original.score") >= 2 ? "#EDBC0C" : "#FF6666"}
        style={{ fontWeight: "bold" }}
      >
        {numeral(get(row, "original.total_value", 0)).format("0,0.00")}
      </LabelText>
    ),
  },
  {
    Header: "Target",
    accessor: "target",
    Cell: ({ row }) => (
      <LabelText style={{ fontWeight: "bold" }}>
        {numeral(get(row, "original.target")).format("0,0.00")}
      </LabelText>
    ),
  },
];

export const Dashboard = () => {
  const [dateFilter, setDateFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [perspectives, setPerspectives] = useState([]);
  const [measures, setMeasures] = useState<any>([]);
  const [fadeIn, setFadeIn] = useState(false);

  const auth = useAuth();
  const { user } = get(auth, "authState");

  const date =
    get(dateFilter, "startDate") && get(dateFilter, "endDate")
      ? `interval=${moment(get(dateFilter, "startDate")).format(
          "YYYY-MM-DD"
        )}/${moment(get(dateFilter, "endDate")).format("YYYY-MM-DD")}`
      : "";

  const {
    data: scorecard,
    error,
    pending,
    reload,
  } = GetHook(
    `${SCORECARD_URL}?owner_id=${get(
          user,
          "id"
        )}&exclude_status=draft&exclude_status=pending-approval&exclude_status=archived`
  );

  const { data: selectedScorecard } = GetHook(
    `${SCORECARD_URL}/${get(scorecard[0], "id")}/summary`
  );

  const fetchData = () => {
    axios
      .get(`${SCORECARD_URL}/${get(scorecard[0], "id")}/perspective`)
      .then((res) => {
        const { returned_resultset } = get(res, "data");
        setPerspectives(returned_resultset);
      });
    axios
      .get(`${SCORECARD_URL}/${get(scorecard[0], "id")}/kpi`)
      .then((res) => {
        const { returned_resultset } = get(res, "data");
        setMeasures(
          returned_resultset
            .filter((kpi) => kpi.periodic_score < 3)
            .sort((a, b) => a.periodic_score - b.periodic_score)
            .sort((a, b) => b.normalised_weight - a.normalised_weight)
        );
      });
  };

  useEffect(() => {
    if (scorecard.length > 0) {
      fetchData();
    }
    setFadeIn(true);

    return () => {};
  }, [scorecard]);

  if (!fadeIn) return null;

  const userId = get(user, "id");
  console.log('Selected Scorecard', selectedScorecard);

  const tabs = [
    { title: "All Measures", content: <UpdateMeasures scorecard_id={selectedScorecard && get(selectedScorecard[0], "id", "")}/>},
    { title: "Approved Measures", content: <MeasuresTab user_id={userId} status="approved" scorecard_id={selectedScorecard && get(selectedScorecard[0], "id", "")} /> },
    { title: "Rejected Measures", content: <MeasuresTab user_id={userId} status="rejected" scorecard_id={selectedScorecard && get(selectedScorecard[0], "id", "")}/> },
    { title: "Pending-Approval Measures", content: <MeasuresTab user_id={userId} status="pending-approval" scorecard_id={selectedScorecard && get(selectedScorecard[0], "id", "")} /> }
  ];
  

  return (
    <FadeInContainer>
      <DashboardWrapper>
        <Header>
          <TitleContainer>
            <TitleText>Dashboard</TitleText>
            <DateText>
              <span>{moment().format("dddd")}</span>,{" "}
              {moment().format("DD MMMM YYYY")}
            </DateText>
          </TitleContainer>
          <DateRangeContainer>
            <Formik initialValues={{}} onSubmit={() => console.log("object")}>
              {({ values }) => {
                setDateFilter(get(values, "date_range"));
                return (
                  <Field
                    name="date_range"
                    label="Time Period"
                    component={DateRangePicker2}
                    backgroundColor="#F5F6FB"
                  />
                );
              }}
            </Formik>
          </DateRangeContainer>
        </Header>

        <MainContent>
          <LeftPanel>
            <ScoreAndStatisticsCard>
              <ScoreSection>
                <SectionTitle>Operating Score</SectionTitle>
                <OperatingScoreWrapper>
                  <DashOperatingScore
                    score={get(selectedScorecard[0], "weighted_periodic_score")}
                    text={false}
                  />
                </OperatingScoreWrapper>
              </ScoreSection>
              <VerticalDivider />
              <StatisticsSection>
                <SectionTitle>Statistics</SectionTitle>
                <OperatingStatisticsWrapper>
                  <OperatingStatistics data={perspectives} />
                </OperatingStatisticsWrapper>
              </StatisticsSection>
            </ScoreAndStatisticsCard>
            <Card>
              <CardHeader>
                <TitleText>Measures that require attention</TitleText>
                {measures.length > 0 &&
                get(scorecard, "[0]owner.id") === get(user, "id") &&
                get(scorecard, "[0]status") === "active" ? (
                  <Button
                    text="Manage measures"
                    onClick={() => setIsModalOpen(true)}
                  />
                ) : null}
              </CardHeader>
              <Divider />
              <LaggingMeasuresContent columns={columns} data={measures} />
            </Card>
          </LeftPanel>
          <RightPanel>
            <Card>
              <TasksContent user={user} />
            </Card>
          </RightPanel>
        </MainContent>
      </DashboardWrapper>
      {isModalOpen && (
        <Modal
          width="90%"
          closeModal={() => setIsModalOpen(false)}
          title=""
          handleSubmit={() => null}
          noFooter
        >
          <TitleText>
            Manage measures
            <span style={{ fontSize: 14, color: "#214160", opacity: 0.5 }}>
              - {selectedScorecard && get(selectedScorecard[0], "name")}
            </span>
          </TitleText>
          <br/>
          <CustomTabs tabs={tabs} />
        </Modal>
      )}
    </FadeInContainer>
  );
};

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const FadeInContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  font-size: 1rem; // Relative unit for responsiveness
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateRangeContainer = styled.div`
  width: 260px;
`;


const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: #d9e2f3;
  margin: 1rem 0;
`;

const ScoreAndStatisticsCard = styled(Card)`
  display: flex;
  padding: 1.5rem;
  height: auto;
  min-height: 200px;
`;

const ScoreSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: auto;
`;

const StatisticsSection = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
`;

const OperatingScoreWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 180px;
  width: 100%;
  height: auto;
  margin-top: 0;
`;


const OperatingStatisticsWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
`;

const VerticalDivider = styled.div`
  width: 1px;
  background-color: #d9e2f3;
  margin: 0 1.5rem;
`;

const DashboardWrapper = styled.div`
  background-color: #f5f6fb;
  padding: 2rem;
  max-width: 100%;
  overflow-x: hidden;
  font-size: 1rem;  // Relative font-size that scales with zoom
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  font-size: 1rem;  // Ensure this adapts to zoom
`;

const DateText = styled.span`
  font-size: 1rem; // Change from 14px to 1rem
  color: #6b7280;
  margin-top: 0.5rem;

  span {
    color: #01A8CE;
  }
`;

const MainContent = styled.main`
  display: flex;
  gap: 1rem;
`;

const LeftPanel = styled.div`
  flex: 3;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const RightPanel = styled.div`
  flex: 1;
  max-width: 300px;
`;
